import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "he" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  he: {
    // Header
    "header.subtitle": "מנתח צ'אטים של WhatsApp עם AI",
    "header.newChat": "צ'אט חדש",
    "header.myChats": "הצ'אטים שלי",
    "header.dashboard": "לוח בקרה",
    "header.admin": "ניהול",
    "header.signOut": "התנתק",
    "header.signIn": "התחבר",

    // Index page
    "index.title": "WhatAI",
    "index.subtitle":
      "מנתח צ'אטים של WhatsApp עם בינה מלאכותית. המר את הצ'אטים שלך לתובנות ניתנות לחיפוש. AI מחלץ תוכן מתמונות, מתמלל אודיו ומנתח מסמכים.",
    "index.getStarted": "התחל עכשיו",
    "index.feature1.title": "צופה צ'אט חכם",
    "index.feature1.desc": "ממשק יפה לגלישה וחיפוש בשיחות שלך",
    "index.feature2.title": "זיהוי תוכן AI",
    "index.feature2.desc": "חילוץ אוטומטי של טקסט מתמונות, תמלול אודיו וניתוח מסמכים",
    "index.feature3.title": "חיפוש מתקדם",
    "index.feature3.desc": "חפש בכל התוכן כולל טקסט שחולץ על ידי AI ממדיה",
    "index.feature4.title": "ציר זמן ותובנות",
    "index.feature4.desc": "הצג דפוסי שיחה וקבל סיכומים שנוצרו על ידי AI",
    "index.cta.title": "מוכן לנתח את הצ'אטים שלך?",
    "index.cta.subtitle": "ייצא את הצ'אט שלך מ-WhatsApp, העלה את קובץ ה-ZIP ותן ל-AI לעשות את הקסם",
    "index.cta.button": "התחל עכשיו",

    // Footer
    "footer.copyright": "© 2025 WhatAI. המידע שלך מעובד בצורה מאובטחת.",
    "footer.disclaimer":
      "WhatAI מספקת כלי ניתוח בלבד. איננו לוקחים אחריות על תוכן הצ'אטים, נתונים מיוצאים או מסקנות כלשהן הנגזרות מהם. כל השימוש והפרשנויות הם באחריות המשתמש בלבד.",

    // VerifyPhone page
    "verify.title": "אמת את הטלפון שלך",
    "verify.subtitle": "נשלח קוד אימות ל-",
    "verify.reward": "🎁 אמת כדי לקבל $10 תקציב ניסיון חינם!",
    "verify.rewardDesc": "השתמש בו כדי לנתח את הצ'אטים שלך מ-WhatsApp",
    "verify.codeLabel": "קוד אימות",
    "verify.codePlaceholder": "הזן קוד בן 6 ספרות",
    "verify.verifyButton": "אמת טלפון",
    "verify.verifying": "מאמת...",
    "verify.resendButton": "שלח קוד מחדש",
    "verify.sending": "שולח...",
    "verify.expiry": "הקוד יפוג בעוד 10 דקות. תעריפי SMS רגילים עשויים לחול.",
    "verify.verified": "אומת!",
    "verify.redirecting": "הטלפון שלך אומת. מפנה...",

    // Admin page
    "admin.title": "פאנל ניהול",
    "admin.budgetRequests": "בקשות תקציב",
    "admin.reviewRequests": "סקור ואשר בקשות תקציב ממשתמשים",
    "admin.noRequests": "לא נמצאו בקשות",
    "admin.date": "תאריך",
    "admin.user": "משתמש",
    "admin.amount": "סכום",
    "admin.status": "סטטוס",
    "admin.actions": "פעולות",
    "admin.approve": "אשר",
    "admin.reject": "דחה",
    "admin.reviewRequest": "סקור בקשה",
    "admin.addNotes": "הוסף הערות אופציונליות להחלטה זו",
    "admin.notesPlaceholder": "הערות מנהל (אופציונלי)",
    "admin.cancel": "ביטול",
    "admin.requestApproved": "בקשה אושרה!",
    "admin.requestRejected": "בקשה נדחתה",
    "admin.accessDenied": "גישה נדחתה",
    "admin.noPrivileges": "אין לך הרשאות מנהל.",
    "admin.usersManagement": "ניהול משתמשים",
    "admin.viewAllUsers": "צפייה בכל המשתמשים וסטטיסטיקות",
    "admin.totalUsers": "סה״כ משתמשים",
    "admin.totalChats": "סה״כ צ׳טים",
    "admin.totalRevenue": "סה״כ הכנסות",
    "admin.noUsers": "אין משתמשים רשומים",
    "admin.joinDate": "תאריך הצטרפות",
    "admin.contact": "פרטי התקשרות",
    "admin.verified": "מאומת",
    "admin.notVerified": "לא מאומת",
    "admin.currentBudget": "תקציב נוכחי",
    "admin.chatsCount": "מספר צ׳טים",
    "admin.totalSpent": "סה״כ הוצאות",
    "admin.requests": "בקשות",
    "admin.addBudget": "הוסף תקציב",
    "admin.addBudgetDesc": "הוסף תקציב ישירות לחשבון המשתמש",
    "admin.newBudget": "תקציב חדש",
    "admin.unknownUser": "משתמש לא ידוע",

    // NotFound page
    "notfound.title": "404",
    "notfound.message": "אופס! הדף לא נמצא",
    "notfound.home": "חזרה לדף הבית",

    // Viewer page
    "viewer.searchPlaceholder": "חפש הודעות ותוכן AI...",
    "viewer.filter": "סינון:",
    "viewer.dateRange": "טווח תאריכים",
    "viewer.date": "תאריך",
    "viewer.images": "תמונות",
    "viewer.img": "תמונה",
    "viewer.audio": "אודיו",
    "viewer.documents": "מסמכים",
    "viewer.docs": "מסמכים",
    "viewer.videos": "וידאו",
    "viewer.contacts": "אנשי קשר",
    "viewer.exportPdf": "ייצא PDF",
    "viewer.pdf": "PDF",
    "viewer.processingComplete": "העיבוד הושלם",
    "viewer.allAnalyzed": "כל הקבצים המצורפים נותחו",
    "viewer.chatNotFound": "צ'אט לא נמצא",
    "viewer.noAccess": "הצ'אט הזה עשוי להיות נמחק או שאין לך גישה אליו",
    "viewer.processingChat": "מעבד ייצוא צ'אט",
    "viewer.parsingMessages": "מנתח הודעות וקבצי מדיה...",
    "viewer.chatLoaded": "הצ'אט נטען!",
    "viewer.foundMessages": "נמצאו {count} הודעות",
    "viewer.allProcessed": "כל הקבצים המצורפים כבר עובדו",
    "viewer.failedToLoad": "נכשל בטעינת הצ'אט",
    "viewer.aiSummary": "סיכום AI",
    "viewer.generating": "יוצר סיכום...",
    "viewer.noSummary": "טוען סיכום...",
    "viewer.downloadPdf": "הורד PDF",
    "viewer.generatingPdf": "יוצר PDF...",
    "viewer.loading": "טוען...",
    "viewer.back": "חזרה",

    // Chat Message
    "message.download": "הורד",
    "message.aiAnalyzing": "AI מנתח תמונה...",
    "message.aiAnalysis": "ניתוח AI:",
    "message.rerunAnalysis": "הרץ מחדש ניתוח AI",
    "message.aiTranscribing": "AI מתמלל אודיו...",
    "message.transcription": "תמלול:",
    "message.rerunTranscription": "הרץ מחדש תמלול AI",
    "message.clickToOpen": "לחץ כדי לפתוח",
    "message.cannotPreview": "לא ניתן להציג תצוגה מקדימה של הקובץ בדפדפן",
    "message.openInNewTab": "פתח בלשונית חדשה",
    "message.aiAnalyzingDoc": "AI מנתח מסמך...",
    "message.content": "תוכן:",
    "message.aiAnalyzingVideo": "AI מנתח וידאו...",
    "message.videoDescription": "תיאור וידאו:",
    "message.aiAnalyzingContact": "AI מנתח איש קשר...",
    "message.contact": "איש קשר",

    // AI Chat Panel
    "aiChat.assistant": "עוזר AI",
    "aiChat.askAbout": "שאל שאלות על השיחה הזו",
    "aiChat.uploadPdf": "העלה PDF לניתוח",
    "aiChat.analyzingPdf": "מנתח PDF",
    "aiChat.clickToView": "לחץ לצפייה ב-PDF",
    "aiChat.onlyPdfAllowed": "רק קבצי PDF מותרים",
    "aiChat.fileTooLarge": "הקובץ גדול מדי (מקסימום 10MB)",
    "aiChat.pdfAnalysisFailed": "ניתוח ה-PDF נכשל",
    "aiChat.pdfAnalysisError": "מצטער, לא הצלחתי לנתח את ה-PDF. אנא נסה שוב.",
    "aiChat.placeholder": "שאל על השיחה...",
    "aiChat.confirmTitle": "אשר שאילתת צ'אט AI",
    "aiChat.confirmDesc": "זה ישלח את ההודעה שלך לעוזר AI לניתוח.",
    "aiChat.error": "מצטער, נתקלתי בשגיאה. אנא נסה שוב.",

    // Share
    "share.title": "שיתוף צ'אט",
    "share.description": "צור קישור שיתוף עם אפשרויות אופציונליות",
    "share.passwordProtection": "הגנת סיסמה",
    "share.passwordDesc": "דורש סיסמה לגישה",
    "share.password": "סיסמה",
    "share.enterPassword": "הזן סיסמה",
    "share.expiration": "תפוגה",
    "share.expirationDesc": "הגדר תאריך תפוגה לקישור",
    "share.expirationDate": "תאריך תפוגה",
    "share.pickDate": "בחר תאריך",
    "share.create": "צור קישור שיתוף",
    "share.cancel": "ביטול",
    "share.shareLink": "קישור שיתוף",
    "share.created": "קישור שיתוף נוצר",
    "share.linkReady": "הקישור שלך מוכן לשיתוף",
    "share.copied": "הועתק!",
    "share.linkCopied": "קישור הועתק ללוח",
    "share.error": "שגיאה",
    "share.failedToCreate": "יצירת קישור שיתוף נכשלה",
    "share.done": "סיום",
    "share.passwordRequired": "דרושה סיסמה",
    "share.expiresOn": "פג תוקף ב-{date}",
    "share.shareChat": "שתף צ'אט",
    "share.notFound": "שיתוף לא נמצא",
    "share.invalidLink": "קישור שיתוף לא חוקי או שפג תוקפו",
    "share.loadError": "שגיאת טעינה",
    "share.failedToLoad": "טעינת צ'אט משותף נכשלה",
    "share.incorrectPassword": "סיסמה שגויה",
    "share.tryAgain": "נסה שוב",
    "share.verificationError": "שגיאת אימות",
    "share.sharedChat": "צ'אט משותף",
    "share.addToMyChats": "הוסף להיסטוריה שלי",
    "share.signInToAdd": "התחבר כדי להוסיף",
    "share.addedToHistory": "נוסף להיסטוריה",
    "share.chatCopied": "הצ'אט הועתק לחשבון שלך",
    "share.addError": "שגיאת הוספה",
    "share.expired": "פג תוקף",
    "share.expiredDesc": "קישור שיתוף זה פג תוקפו",
    "share.goHome": "עבור לדף הבית",
    "share.enterPasswordDesc": "צ'אט זה מוגן בסיסמה",
    "share.unlock": "פתח",

    // Timeline
    "timeline.title": "ציר זמן פעילות",
    "timeline.noData": "אין נתוני פעילות זמינים",
    "timeline.peak": "פעילות שיא",
    "timeline.high": "פעילות גבוהה",
    "timeline.regular": "פעילות רגילה",
    "timeline.light": "פעילות קלה",

    // Multi-view page
    "multiView.title": "תצוגה מרובה",
    "multiView.chats": "צ'אטים",
    "multiView.searchPlaceholder": "חפש בכל הצ'אטים...",
    "multiView.selectChats": "בחר צ'אטים להשוואה",
    "multiView.selectDescription": "בחר בין 2 ל-4 צ'אטים כדי להציג אותם זה לצד זה מסונכרנים לפי זמן",
    "multiView.selectAtLeast2": "אנא בחר לפחות 2 צ'אטים",
    "multiView.selectMaximum4": "ניתן לבחור עד 4 צ'אטים",
    "multiView.selected": "נבחרו",
    "multiView.compare": "השווה צ'אטים",

    // Budget Manager
    "budget.title": "ניהול תקציב AI",
    "budget.desc": "שלם מראש עבור פעולות AI. פעולות ירוצו רק אם יש לך תקציב מספיק.",
    "budget.currentBalance": "יתרה נוכחית",
    "budget.deductedAsUsed": "מנוכה ככל שאתה משתמש בתכונות AI",
    "budget.lowWarning": "התקציב שלך נמוך. הוסף כסף כדי להמשיך להשתמש בתכונות AI.",
    "budget.addToBudget": "הוסף לתקציב",
    "budget.addFunds": "הוסף כסף",
    "budget.manualAdd": "הוספה ידנית",
    "budget.requestBudget": "בקש תקציב",
    "budget.purchaseCredits": "רכוש קרדיטים",
    "budget.manualAddition": "הוספת תקציב ידנית",
    "budget.forTesting": "למטרות בדיקה - הוסף תקציב ידנית ללא תשלום",
    "budget.addAmount": "הוסף סכום",
    "budget.info1": "• תקציב מנוכה אוטומטית עבור שאילתות צ'אט, ניתוח תמונות ותמלול אודיו",
    "budget.info2": "• צפה בניתוח הוצאות מפורט בלוח הבקרה",
    "budget.info3": "• פעולות ייחסמו אם התקציב לא מספיק",
    "budget.requestTitle": "בקש תקציב",
    "budget.requestDesc": "שלח בקשה להוספת תקציב לחשבון שלך. מנהל יסקור ויאשר אותה.",
    "budget.submitRequest": "שלח בקשה",
    "budget.purchaseTitle": "רכוש תקציב AI",
    "budget.purchaseDesc": "הוסף עוד תקציב כדי להמשיך להשתמש בתכונות AI. תשלום מאובטח דרך PayPal.",
    "budget.starter": "מתחיל",
    "budget.basic": "בסיסי",
    "budget.pro": "מקצועי",
    "budget.premium": "פרימיום",
    "budget.enterprise": "ארגוני",
    "budget.popular": "פופולרי",
    "budget.aiBudget": "תקציב AI",
    "budget.selectPackage": "בחר חבילה",
    "budget.purchaseInfo1": "• עיבוד תשלום מאובטח דרך PayPal",
    "budget.purchaseInfo2": "• תקציב מתווסף מיד לאחר תשלום מוצלח",
    "budget.purchaseInfo3": "• כל המחירים בדולר אמריקאי",
    "budget.completePayment": "השלם תשלום",
    "budget.purchasing": "רוכש {label} חבילה - ${price}",
    "budget.youWillReceive": "תקבל",
    "budget.addedToBalance": "מתווסף לתקציב ה-AI שלך",
    "budget.backToPackages": "חזרה לחבילות",

    // Cost Tracker
    "cost.totalAiCost": "עלות AI כוללת",
    "cost.initialProcessing": "עיבוד ראשוני",
    "cost.chatQueries": "שאילתות צ'אט",
    "cost.rerunCaptions": "הרצת כיתובים מחדש",
    "cost.updatesRealtime": "עלויות מתעדכנות בזמן אמת",

    // Cost Estimator
    "cost.estimation": "אומדן עלות",
    "cost.totalApiCalls": 'סה"כ קריאות API:',
    "cost.imageAnalysis": "ניתוח תמונות",
    "cost.audioTranscription": "תמלול אודיו",
    "cost.videoAnalysis": "ניתוח סרטונים",
    "cost.documentAnalysis": "ניתוח מסמכים",
    "cost.contactAnalysis": "ניתוח אנשי קשר",
    "cost.summaryGeneration": "יצירת סיכום",
    "cost.calls": "קריאות",
    "cost.call": "קריאה",

    // Cost Confirm Dialog
    "cost.estimatedCost": "עלות משוערת:",
    "cost.actualMayVary": "* העלות בפועל עשויה להשתנות בהתאם למורכבות התוכן",
    "cost.currentBudget": "תקציב נוכחי",
    "cost.afterOperation": "לאחר הפעולה",
    "cost.insufficientBudget": "תקציב לא מספיק. אתה צריך {amount} נוסף כדי להמשיך.",
    "cost.insufficientBudgetBtn": "תקציב לא מספיק",
    "cost.confirmProceed": "אשר והמשך",

    // Toasts
    "toast.budgetAdded": "נוסף {amount} לתקציב שלך",
    "toast.insufficientBudget": "תקציב לא מספיק. אנא הוסף כסף כדי להמשיך.",
    "toast.budgetFailed": "נכשל בעדכון תקציב",
    "toast.budgetProcessFailed": "נכשל בעיבוד תקציב",
    "toast.requestSubmitted": "בקשת תקציב נשלחה! מנהל יסקור אותה בקרוב.",
    "toast.requestFailed": "נכשל בשליחת בקשה",
    "toast.paymentSuccess": "נוסף בהצלחה {amount} לתקציב שלך!",
    "toast.paymentFailed": "עיבוד תשלום נכשל. אנא נסה שוב.",
    "toast.mustBeLoggedIn": "עליך להיות מחובר",
    "toast.validAmount": "אנא הזן סכום חוקי",
    "toast.maxSingleAdd": "הוספה בודדת מקסימלית היא $1000",
    "toast.loadBudgetFailed": "נכשל בטעינת תקציב",
    "toast.alreadyVerified": "הטלפון כבר אומת!",
    "toast.verificationSent": "קוד אימות נשלח לטלפון שלך!",
    "toast.verificationFailed": "נכשל בשליחת קוד אימות",
    "toast.invalidCode": "קוד אימות לא תקין",
    "toast.verificationSuccess": "🎉 הטלפון שלך אומת בהצלחה!",
    "toast.costDataFailed": "נכשל בטעינת נתוני עלות",
    "toast.chatHistoryFailed": "נכשל בטעינת היסטוריית צ'אט",
    "toast.chatDeleted": "הצ'אט נמחק בהצלחה",
    "toast.chatDeleteFailed": "נכשל במחיקת הצ'אט",
    "toast.chatRestored": "הצ'אט שוחזר בהצלחה",
    "toast.chatRestoreFailed": "נכשל בשחזור הצ'אט",

    // Auth page toasts and loading
    "loading.general": "טוען...",
    "loading.connecting": "מתחבר...",
    "loading.creatingAccount": "יוצר חשבון...",
    "toast.signInFailed": "ההתחברות נכשלה",
    "toast.invalidCredentials": "מספר טלפון או סיסמה לא תקינים. נסה שוב.",
    "toast.welcomeBack": "ברוך שובך!",
    "toast.successfullySignedIn": "התחברת בהצלחה",
    "toast.validationError": "שגיאת ולידציה",
    "toast.passwordsDontMatch": "הסיסמאות לא תואמות",
    "toast.ensurePasswordsMatch": "ודא שתי הסיסמאות זהות",
    "toast.phoneAlreadyRegistered": "מספר הטלפון כבר רשום",
    "toast.phoneInUse": "מספר טלפון זה כבר בשימוש. התחבר במקום.",
    "toast.signUpFailed": "ההרשמה נכשלה",
    "toast.accountCreated": "החשבון נוצר! 📱",
    "toast.checkPhoneVerification": "בדוק בטלפון שלך קוד אימות כדי לקבל $10 תקציב ניסיון!",
    "toast.accountCreatedGoVerify": "החשבון נוצר!",
    "toast.goToVerificationPage": "עבור לדף האימות לאימות הטלפון שלך.",

    // Upload page toasts and loading
    "toast.fileAnalyzed": "הקובץ נותח",
    "toast.foundMediaFiles": "נמצאו {images} תמונות ו-{audio} קבצי אודיו",
    "toast.analysisWarning": "אזהרת ניתוח",
    "toast.couldNotAnalyze": "לא ניתן לנתח את תוכן הקובץ. העלה כדי להמשיך.",
    "toast.invalidFile": "קובץ לא תקין",
    "toast.uploadZipFile": "העלה קובץ ZIP המכיל את ייצוא צ'אט WhatsApp שלך",
    "toast.missingFile": "קובץ חסר",
    "toast.pleaseUploadZip": "העלה קובץ ZIP",
    "toast.analysisRequired": "נדרש ניתוח",
    "toast.waitForAnalysis": "המתן לסיום ניתוח הקובץ",
    "toast.insufficientBudgetUpload": "תקציב לא מספיק",
    "toast.addFundsForChat": "הוסף כספים לתקציב שלך לפני עיבוד צ'אט זה.",
    "toast.budgetError": "שגיאה",
    "toast.failedProcessBudget": "נכשל בעיבוד התקציב",
    "toast.uploading": "מעלה...",
    "toast.savingChatExport": "שומר את ייצוא הצ'אט שלך...",
    "toast.uploadSuccessful": "ההעלאה הצליחה",
    "toast.processingYourChat": "מעבד את הצ'אט שלך...",
    "toast.uploadFailed": "ההעלאה נכשלה",
    "toast.errorProcessingFile": "הייתה שגיאה בעיבוד הקובץ שלך",
    "toast.chatRestoredSuccess": "הצ'אט שלך שוחזר בהצלחה",
    "toast.errorRestoringChat": "נכשל בשחזור הצ'אט",

    // Viewer page loading and toasts
    "loading.initializing": "מאתחל...",
    "loading.uploadingMediaFiles": "מעלה קבצי מדיה...",
    "toast.chatNotFound": "הצ'אט לא נמצא",
    "toast.chatMayBeDeleted": "צ'אט זה אולי נמחק או אין לך גישה אליו",
    "toast.processingChatExportFile": "מעבד ייצוא צ'אט",
    "toast.parsingMessages": "מפענח הודעות וקבצי מדיה...",
    "toast.chatLoaded": "הצ'אט נטען!",
    "toast.foundMessages": "נמצאו {count} הודעות",
    "toast.allAttachmentsProcessed": "כל הקבצים המצורפים עובדו",
    "toast.failedToLoadChat": "נכשל בטעינת הצ'אט",
    "toast.processingComplete": "העיבוד הושלם",
    "toast.allAttachmentsAnalyzed": "כל הקבצים המצורפים נותחו",
    "toast.insufficientBudgetRerun": "תקציב לא מספיק",
    "toast.addFundsRerun": "הוסף כספים לתקציב שלך כדי להפעיל מחדש כיתוב.",
    "toast.rerunningCaption": "מפעיל מחדש כיתוב...",
    "toast.rerunComplete": "ההפעלה מחדש הושלמה",
    "toast.captionUpdated": "הכיתוב עודכן",
    "toast.rerunFailed": "ההפעלה מחדש נכשלה",
    "toast.failedRerunCaption": "נכשל בהפעלת הכיתוב מחדש",

    // Viewer page UI
    "viewer.chatAnalysis": "ניתוח צ'אט",
    "viewer.extractingMessages": "מחלץ הודעות וקבצי מדיה",
    "viewer.messages": "הודעות",
    "viewer.showingMessages": "מציג {filtered} מתוך {total} הודעות",
    "viewer.noMessages": "אין הודעות להצגה",
    "viewer.uploadChat": "העלה צ'אט",
    "viewer.noMatchingMessages": "אין הודעות התואמות את המסננים",
    "viewer.clearFilters": "נקה מסננים",
    "viewer.messagesLabel": "הודעות",
    "viewer.imagesLabel": "תמונות",
    "viewer.audioLabel": "אודיו",
    "viewer.documentsLabel": "מסמכים",
    "viewer.aiSummaryTitle": "סיכום AI",
    "viewer.analyzingConversation": "מנתח שיחה...",
    "viewer.noSummaryYet": "אין סיכום זמין עדיין.",
    "viewer.rerunSummary": "הרץ מחדש",
    "viewer.regeneratingSummary": "יוצר סיכום מחדש...",
    "viewer.summaryRegenerated": "הסיכום נוצר מחדש בהצלחה",
    "viewer.rerunSummaryError": "שגיאה ביצירת סיכום מחדש",
    "viewer.confirmRerunTitle": "אשר הרצת כיתוב מחדש",
    "viewer.confirmRerunDesc": "זה יצור מחדש את ניתוח ה-AI עבור הקובץ המצורף הזה.",
    "viewer.popupBlocked": "חסימת חלונות קופצים",
    "viewer.allowPopups": "אנא אפשר חלונות קופצים כדי לצפות ב-PDF",
    "viewer.pdfReady": "PDF מוכן",
    "viewer.previewOpened": "תצוגה מקדימה נפתחה בכרטיסייה חדשה. תוכל להוריד אותה משם.",
    "viewer.exportError": "שגיאה",
    "viewer.exportFailed": "נכשל בייצוא PDF",
    "viewer.generatingSummary": "יוצר סיכום AI...",
    "viewer.pageOf": "עמוד {current} מתוך {total}",
    "viewer.whatsappChat": "צ'אט WhatsApp",

    // Advanced Settings
    "advancedSettings.title": "הגדרות מתקדמות",
    "advancedSettings.audioProcessing": "עיבוד אודיו",
    "advancedSettings.whisperModel": "מודל Whisper",
    "advancedSettings.whisperTiny": "זעיר (מהיר ביותר, איכות נמוכה יותר)",
    "advancedSettings.whisperBase": "בסיסי",
    "advancedSettings.whisperSmall": "קטן (מומלץ)",
    "advancedSettings.whisperMedium": "בינוני",
    "advancedSettings.whisperLarge": "גדול (איטי ביותר, איכות מעולה)",
    "advancedSettings.useFasterWhisper": "השתמש ב-Whisper מהיר יותר",
    "advancedSettings.maxAudioLength": "אורך אודיו מקסימלי (שניות, 0 = ללא הגבלה)",
    "advancedSettings.visionAndImages": "ראייה ותמונות",
    "advancedSettings.visionBackend": "מנוע ראייה",
    "advancedSettings.visionAuto": "אוטומטי",
    "advancedSettings.visionOpenAI": "OpenAI",
    "advancedSettings.visionBLIP": "BLIP",
    "advancedSettings.visionNone": "ללא",
    "advancedSettings.maxImageWidth": "רוחב תמונה מקסימלי (פיקסלים)",
    "advancedSettings.inlineAssets": "נכסים מוטמעים (Base64, HTML גדול יותר)",
    "advancedSettings.processingOptions": "אפשרויות עיבוד",
    "advancedSettings.debugLevel": "רמת ניפוי שגיאות",
    "advancedSettings.debugDebug": "ניפוי שגיאות",
    "advancedSettings.debugInfo": "מידע",
    "advancedSettings.debugWarning": "אזהרה",
    "advancedSettings.debugError": "שגיאה",
    "advancedSettings.debugCritical": "קריטי",
    "advancedSettings.skipSummary": "דלג על יצירת סיכום",
    "advancedSettings.rateLimiting": "הגבלת קצב",
    "advancedSettings.minDelay": "עיכוב מינימלי (שניות)",
    "advancedSettings.maxDelay": "עיכוב מקסימלי (שניות)",
    "advancedSettings.maxRetries": "ניסיונות חוזרים מקסימליים",
    "advancedSettings.backoffMultiplier": "מכפיל עיכוב הדרגתי",
  },
  en: {
    // Header
    "header.subtitle": "WhatsApp chat analyzer with AI",
    "header.newChat": "New Chat",
    "header.myChats": "My Chats",
    "header.dashboard": "Dashboard",
    "header.admin": "Admin",
    "header.signOut": "Sign Out",
    "header.signIn": "Sign In",

    // Index page
    "index.title": "WhatAI",
    "index.subtitle":
      "WhatsApp chat analyzer with AI. Transform your chats into searchable insights. AI extracts content from images, transcribes audio, and analyzes documents.",
    "index.getStarted": "Get Started",
    "index.feature1.title": "Smart Chat Viewer",
    "index.feature1.desc": "Beautiful interface to browse and search through your conversations",
    "index.feature2.title": "AI Content Recognition",
    "index.feature2.desc": "Automatically extract text from images, transcribe audio, and analyze documents",
    "index.feature3.title": "Advanced Search",
    "index.feature3.desc": "Search across all content including AI-extracted text from media",
    "index.feature4.title": "Timeline & Insights",
    "index.feature4.desc": "Visualize conversation patterns and get AI-generated summaries",
    "index.cta.title": "Ready to analyze your chats?",
    "index.cta.subtitle": "Export your WhatsApp chat, upload the ZIP file, and let AI do the magic",
    "index.cta.button": "Start Now",

    // Footer
    "footer.copyright": "© 2025 WhatAI. Your data is processed securely.",
    "footer.disclaimer":
      "WhatAI provides analysis tools only. We do not take responsibility for the content of chats, exported data, or any conclusions derived from them. All usage and interpretations are the sole responsibility of the user.",

    // VerifyPhone page
    "verify.title": "Verify Your Phone",
    "verify.subtitle": "We'll send a verification code to",
    "verify.reward": "🎁 Verify to get $10 FREE trial budget!",
    "verify.rewardDesc": "Use it to analyze your WhatsApp chats",
    "verify.codeLabel": "Verification Code",
    "verify.codePlaceholder": "Enter 6-digit code",
    "verify.verifyButton": "Verify Phone",
    "verify.verifying": "Verifying...",
    "verify.resendButton": "Resend Code",
    "verify.sending": "Sending...",
    "verify.expiry": "Code expires in 10 minutes. Standard SMS rates may apply.",
    "verify.verified": "Verified!",
    "verify.redirecting": "Your phone is verified. Redirecting...",

    // Admin page
    "admin.title": "Admin Panel",
    "admin.budgetRequests": "Budget Purchase Requests",
    "admin.reviewRequests": "Review and approve budget requests from users",
    "admin.noRequests": "No requests found",
    "admin.date": "Date",
    "admin.user": "User",
    "admin.amount": "Amount",
    "admin.status": "Status",
    "admin.actions": "Actions",
    "admin.approve": "Approve",
    "admin.reject": "Reject",
    "admin.reviewRequest": "Review Request",
    "admin.addNotes": "Add optional notes for this decision",
    "admin.notesPlaceholder": "Admin notes (optional)",
    "admin.cancel": "Cancel",
    "admin.requestApproved": "Request approved!",
    "admin.requestRejected": "Request rejected",
    "admin.accessDenied": "Access Denied",
    "admin.noPrivileges": "You don't have admin privileges.",
    "admin.usersManagement": "Users Management",
    "admin.viewAllUsers": "View all users and statistics",
    "admin.totalUsers": "Total Users",
    "admin.totalChats": "Total Chats",
    "admin.totalRevenue": "Total Revenue",
    "admin.noUsers": "No registered users",
    "admin.joinDate": "Join Date",
    "admin.contact": "Contact Info",
    "admin.verified": "Verified",
    "admin.notVerified": "Not Verified",
    "admin.currentBudget": "Current Budget",
    "admin.chatsCount": "Chats Count",
    "admin.totalSpent": "Total Spent",
    "admin.requests": "Requests",
    "admin.pending": "Pending",
    "admin.approved": "Approved",
    "admin.rejected": "Rejected",
    "admin.addBudget": "Add Budget",
    "admin.addBudgetDesc": "Add budget directly to user's account",
    "admin.newBudget": "New budget",
    "admin.unknownUser": "Unknown User",

    // NotFound page
    "notfound.title": "404",
    "notfound.message": "Oops! Page not found",
    "notfound.home": "Return to Home",

    // Viewer page
    "viewer.searchPlaceholder": "Search messages and AI content...",
    "viewer.filter": "Filter:",
    "viewer.dateRange": "Date Range",
    "viewer.date": "Date",
    "viewer.images": "Images",
    "viewer.img": "Img",
    "viewer.audio": "Audio",
    "viewer.documents": "Documents",
    "viewer.docs": "Docs",
    "viewer.videos": "Videos",
    "viewer.contacts": "Contacts",
    "viewer.exportPdf": "Export PDF",
    "viewer.pdf": "PDF",
    "viewer.processingComplete": "Processing complete",
    "viewer.allAnalyzed": "All attachments analyzed",
    "viewer.chatNotFound": "Chat not found",
    "viewer.noAccess": "This chat may have been deleted or you don't have access to it",
    "viewer.processingChat": "Processing chat export",
    "viewer.parsingMessages": "Parsing messages and media files...",
    "viewer.chatLoaded": "Chat loaded!",
    "viewer.foundMessages": "Found {count} messages",
    "viewer.allProcessed": "All attachments already processed",
    "viewer.failedToLoad": "Failed to load chat",
    "viewer.aiSummary": "AI Summary",
    "viewer.generating": "Generating summary...",
    "viewer.noSummary": "Loading summary...",
    "viewer.downloadPdf": "Download PDF",
    "viewer.generatingPdf": "Generating PDF...",

    // Chat Message
    "message.download": "Download",
    "message.aiAnalyzing": "AI analyzing image...",
    "message.aiAnalysis": "AI Analysis:",
    "message.rerunAnalysis": "Rerun AI analysis",
    "message.aiTranscribing": "AI transcribing audio...",
    "message.transcription": "Transcription:",
    "message.rerunTranscription": "Rerun AI transcription",
    "message.clickToOpen": "Click to open",
    "message.cannotPreview": "Cannot preview this file in browser",
    "message.openInNewTab": "Open in New Tab",
    "message.aiAnalyzingDoc": "AI analyzing document...",
    "message.content": "Content:",
    "message.aiAnalyzingVideo": "AI analyzing video...",
    "message.videoDescription": "Video description:",
    "message.aiAnalyzingContact": "AI analyzing contact...",
    "message.contact": "Contact",

    // AI Chat Panel
    "aiChat.assistant": "AI Assistant",
    "aiChat.askAbout": "Ask questions about this conversation",
    "aiChat.uploadPdf": "Upload PDF to analyze",
    "aiChat.analyzingPdf": "Analyzing PDF",
    "aiChat.clickToView": "Click to view PDF",
    "aiChat.onlyPdfAllowed": "Only PDF files are allowed",
    "aiChat.fileTooLarge": "File is too large (max 10MB)",
    "aiChat.pdfAnalysisFailed": "Failed to analyze PDF",
    "aiChat.pdfAnalysisError": "Sorry, I couldn't analyze the PDF. Please try again.",
    "aiChat.placeholder": "Ask about the conversation...",
    "aiChat.confirmTitle": "Confirm AI Chat Query",
    "aiChat.confirmDesc": "This will send your message to the AI assistant for analysis.",
    "aiChat.error": "Sorry, I encountered an error. Please try again.",

    // Share
    "share.title": "Share Chat",
    "share.description": "Create a shareable link with optional settings",
    "share.passwordProtection": "Password Protection",
    "share.passwordDesc": "Require a password to access",
    "share.password": "Password",
    "share.enterPassword": "Enter password",
    "share.expiration": "Expiration",
    "share.expirationDesc": "Set an expiration date for the link",
    "share.expirationDate": "Expiration Date",
    "share.pickDate": "Pick a date",
    "share.create": "Create Share Link",
    "share.cancel": "Cancel",
    "share.shareLink": "Share Link",
    "share.created": "Share Link Created",
    "share.linkReady": "Your link is ready to share",
    "share.copied": "Copied!",
    "share.linkCopied": "Link copied to clipboard",
    "share.error": "Error",
    "share.failedToCreate": "Failed to create share link",
    "share.done": "Done",
    "share.passwordRequired": "Password Required",
    "share.expiresOn": "Expires on {date}",
    "share.shareChat": "Share Chat",
    "share.notFound": "Share Not Found",
    "share.invalidLink": "Invalid or expired share link",
    "share.loadError": "Load Error",
    "share.failedToLoad": "Failed to load shared chat",
    "share.incorrectPassword": "Incorrect Password",
    "share.tryAgain": "Please try again",
    "share.verificationError": "Verification Error",
    "share.sharedChat": "Shared Chat",
    "share.addToMyChats": "Add to My History",
    "share.signInToAdd": "Sign In to Add",
    "share.addedToHistory": "Added to History",
    "share.chatCopied": "Chat copied to your account",
    "share.addError": "Add Error",
    "share.expired": "Expired",
    "share.expiredDesc": "This share link has expired",
    "share.goHome": "Go Home",
    "share.enterPasswordDesc": "This chat is password protected",
    "share.unlock": "Unlock",

    // Timeline
    "timeline.title": "Activity Timeline",
    "timeline.noData": "No activity data available",
    "timeline.peak": "Peak activity",
    "timeline.high": "High activity",
    "timeline.regular": "Regular activity",
    "timeline.light": "Light activity",

    // Multi-view page
    "multiView.title": "Multi View",
    "multiView.chats": "chats",
    "multiView.searchPlaceholder": "Search across all chats...",
    "multiView.selectChats": "Select Chats to Compare",
    "multiView.selectDescription": "Select between 2 to 4 chats to view them side by side synchronized by time",
    "multiView.selectAtLeast2": "Please select at least 2 chats",
    "multiView.selectMaximum4": "You can select up to 4 chats",
    "multiView.selected": "selected",
    "multiView.compare": "Compare Chats",

    // Budget Manager
    "budget.title": "AI Budget Management",
    "budget.desc": "Pre-pay for AI operations. Operations will only run if you have sufficient budget.",
    "budget.currentBalance": "Current Balance",
    "budget.deductedAsUsed": "Deducted as you use AI features",
    "budget.lowWarning": "Your budget is low. Add funds to continue using AI features.",
    "budget.addToBudget": "Add to Budget",
    "budget.addFunds": "Add Funds",
    "budget.manualAdd": "Manual Add",
    "budget.requestBudget": "Request Budget",
    "budget.purchaseCredits": "Purchase Credits",
    "budget.manualAddition": "Manual Budget Addition",
    "budget.forTesting": "For testing purposes - manually add budget without payment",
    "budget.addAmount": "Add Amount",
    "budget.info1": "• Budget is deducted automatically for chat queries, image analysis, and audio transcription",
    "budget.info2": "• View detailed spending analytics in the Dashboard",
    "budget.info3": "• Operations will be blocked if budget is insufficient",
    "budget.requestTitle": "Request Budget",
    "budget.requestDesc": "Submit a request to add budget to your account. An admin will review and approve it.",
    "budget.submitRequest": "Submit Request",
    "budget.purchaseTitle": "Purchase AI Budget",
    "budget.purchaseDesc": "Add more budget to continue using AI features. Secure payment via PayPal.",
    "budget.starter": "Starter",
    "budget.basic": "Basic",
    "budget.pro": "Pro",
    "budget.premium": "Premium",
    "budget.enterprise": "Enterprise",
    "budget.popular": "Popular",
    "budget.aiBudget": "AI Budget",
    "budget.selectPackage": "Select Package",
    "budget.purchaseInfo1": "• Secure payment processing via PayPal",
    "budget.purchaseInfo2": "• Budget is added immediately after successful payment",
    "budget.purchaseInfo3": "• All prices are in USD",
    "budget.completePayment": "Complete Payment",
    "budget.purchasing": "Purchasing {label} Package - ${price}",
    "budget.youWillReceive": "You will receive",
    "budget.addedToBalance": "Added to your AI Budget",
    "budget.backToPackages": "Back to Packages",

    // Cost Tracker
    "cost.totalAiCost": "Total AI Cost",
    "cost.initialProcessing": "Initial Processing",
    "cost.chatQueries": "Chat Queries",
    "cost.rerunCaptions": "Rerun Captions",
    "cost.updatesRealtime": "Costs update in real-time",

    // Cost Estimator
    "cost.estimation": "Cost Estimation",
    "cost.totalApiCalls": "Total API Calls:",
    "cost.imageAnalysis": "Image Analysis",
    "cost.audioTranscription": "Audio Transcription",
    "cost.summaryGeneration": "Summary Generation",
    "cost.calls": "calls",
    "cost.call": "call",
    "cost.disclaimer":
      "* Estimated cost based on Lovable AI pricing. Actual cost may vary based on content complexity.",

    // Cost Confirm Dialog
    "cost.estimatedCost": "Estimated Cost:",
    "cost.actualMayVary": "* Actual cost may vary based on content complexity",
    "cost.currentBudget": "Current Budget",
    "cost.afterOperation": "After Operation",
    "cost.insufficientBudget": "Insufficient budget. You need {amount} more to proceed.",
    "cost.insufficientBudgetBtn": "Insufficient Budget",
    "cost.confirmProceed": "Confirm & Proceed",

    // Toasts
    "toast.budgetAdded": "Added {amount} to your budget",
    "toast.insufficientBudget": "Insufficient budget. Please add funds to continue.",
    "toast.budgetFailed": "Failed to add budget",
    "toast.budgetProcessFailed": "Failed to process budget",
    "toast.requestSubmitted": "Budget request submitted! Admin will review it soon.",
    "toast.requestFailed": "Failed to submit request",
    "toast.paymentSuccess": "Successfully added {amount} to your budget!",
    "toast.paymentFailed": "Payment processing failed. Please try again.",
    "toast.mustBeLoggedIn": "You must be logged in",
    "toast.validAmount": "Please enter a valid amount",
    "toast.maxSingleAdd": "Maximum single addition is $1000",
    "toast.loadBudgetFailed": "Failed to load budget",
    "toast.alreadyVerified": "Phone already verified!",
    "toast.verificationSent": "Verification code sent to your phone!",
    "toast.verificationFailed": "Failed to send verification code",
    "toast.invalidCode": "Invalid verification code",
    "toast.verificationSuccess": "🎉 Your phone has been verified successfully!",
    "toast.costDataFailed": "Failed to load cost data",
    "toast.chatHistoryFailed": "Failed to load chat history",
    "toast.chatDeleted": "Chat deleted successfully",
    "toast.chatDeleteFailed": "Failed to delete chat",
    "toast.chatRestored": "Chat restored successfully",
    "toast.chatRestoreFailed": "Failed to restore chat",

    // Auth page toasts and loading
    "loading.general": "Loading...",
    "loading.connecting": "Connecting...",
    "loading.creatingAccount": "Creating account...",
    "toast.signInFailed": "Sign in failed",
    "toast.invalidCredentials": "Invalid phone number or password. Please try again.",
    "toast.welcomeBack": "Welcome back!",
    "toast.successfullySignedIn": "Successfully signed in",
    "toast.validationError": "Validation error",
    "toast.passwordsDontMatch": "Passwords don't match",
    "toast.ensurePasswordsMatch": "Please make sure both passwords are the same",
    "toast.phoneAlreadyRegistered": "Phone number already registered",
    "toast.phoneInUse": "This phone number is already in use. Please sign in instead.",
    "toast.signUpFailed": "Sign up failed",
    "toast.accountCreated": "Account created! 📱",
    "toast.checkPhoneVerification": "Check your phone for a verification code to get $10 trial budget!",
    "toast.accountCreatedGoVerify": "Account created!",
    "toast.goToVerificationPage": "Please go to verification page to verify your phone.",

    // Upload page toasts and loading
    "toast.fileAnalyzed": "File analyzed",
    "toast.foundMediaFiles": "Found {images} images and {audio} audio files",
    "toast.analysisWarning": "Analysis warning",
    "toast.couldNotAnalyze": "Could not analyze file contents. Upload to continue.",
    "toast.invalidFile": "Invalid file",
    "toast.uploadZipFile": "Please upload a ZIP file containing your WhatsApp chat export",
    "toast.missingFile": "Missing file",
    "toast.pleaseUploadZip": "Please upload a ZIP file",
    "toast.analysisRequired": "Analysis required",
    "toast.waitForAnalysis": "Please wait for file analysis to complete",
    "toast.insufficientBudgetUpload": "Insufficient Budget",
    "toast.addFundsForChat": "Please add funds to your budget before processing this chat.",
    "toast.budgetError": "Error",
    "toast.failedProcessBudget": "Failed to process budget",
    "toast.uploading": "Uploading...",
    "toast.savingChatExport": "Saving your chat export...",
    "toast.uploadSuccessful": "Upload successful",
    "toast.processingYourChat": "Processing your chat...",
    "toast.uploadFailed": "Upload failed",
    "toast.errorProcessingFile": "There was an error processing your file",
    "toast.chatRestoredSuccess": "Your chat has been restored successfully",
    "toast.errorRestoringChat": "Failed to restore chat",

    // Viewer page loading and toasts
    "loading.initializing": "Initializing...",
    "loading.uploadingMediaFiles": "Uploading media files...",
    "toast.chatNotFound": "Chat not found",
    "toast.chatMayBeDeleted": "This chat may have been deleted or you don't have access to it",
    "toast.processingChatExportFile": "Processing chat export",
    "toast.parsingMessages": "Parsing messages and media files...",
    "toast.chatLoaded": "Chat loaded!",
    "toast.foundMessages": "Found {count} messages",
    "toast.allAttachmentsProcessed": "All attachments already processed",
    "toast.failedToLoadChat": "Failed to load chat",
    "toast.processingComplete": "Processing complete",
    "toast.allAttachmentsAnalyzed": "All attachments analyzed",
    "toast.insufficientBudgetRerun": "Insufficient Budget",
    "toast.addFundsRerun": "Please add funds to your budget to rerun caption.",
    "toast.rerunningCaption": "Rerunning caption...",
    "toast.rerunComplete": "Rerun complete",
    "toast.captionUpdated": "Caption updated",
    "toast.rerunFailed": "Rerun failed",
    "toast.failedRerunCaption": "Failed to rerun caption",

    // Viewer page UI
    "viewer.chatAnalysis": "Chat Analysis",
    "viewer.extractingMessages": "Extracting messages and media files",
    "viewer.messages": "Messages",
    "viewer.showingMessages": "Showing {filtered} of {total} messages",
    "viewer.noMessages": "No messages to display",
    "viewer.uploadChat": "Upload Chat",
    "viewer.noMatchingMessages": "No messages match your filters",
    "viewer.clearFilters": "Clear Filters",
    "viewer.messagesLabel": "Messages",
    "viewer.imagesLabel": "Images",
    "viewer.audioLabel": "Audio",
    "viewer.documentsLabel": "Documents",
    "viewer.aiSummaryTitle": "AI Summary",
    "viewer.analyzingConversation": "Analyzing conversation...",
    "viewer.noSummaryYet": "No summary available yet.",
    "viewer.rerunSummary": "Rerun",
    "viewer.regeneratingSummary": "Regenerating summary...",
    "viewer.summaryRegenerated": "Summary regenerated successfully",
    "viewer.rerunSummaryError": "Error regenerating summary",
    "viewer.confirmRerunTitle": "Confirm Rerun Caption",
    "viewer.confirmRerunDesc": "This will regenerate the AI analysis for this attachment.",
    "viewer.popupBlocked": "Pop-up blocked",
    "viewer.allowPopups": "Please allow pop-ups to preview the PDF",
    "viewer.pdfReady": "PDF ready",
    "viewer.previewOpened": "Preview opened in new tab. You can download it from there.",
    "viewer.exportError": "Error",
    "viewer.exportFailed": "Failed to export PDF",
    "viewer.generatingSummary": "Generating AI summary...",
    "viewer.pageOf": "Page {current} of {total}",
    "viewer.whatsappChat": "WhatsApp Chat",

    // Advanced Settings
    "advancedSettings.title": "Advanced Settings",
    "advancedSettings.audioProcessing": "Audio Processing",
    "advancedSettings.whisperModel": "Whisper Model",
    "advancedSettings.whisperTiny": "Tiny (Fastest, Lower Quality)",
    "advancedSettings.whisperBase": "Base",
    "advancedSettings.whisperSmall": "Small (Recommended)",
    "advancedSettings.whisperMedium": "Medium",
    "advancedSettings.whisperLarge": "Large (Slowest, Best Quality)",
    "advancedSettings.useFasterWhisper": "Use Faster Whisper",
    "advancedSettings.maxAudioLength": "Max Audio Length (seconds, 0 = no limit)",
    "advancedSettings.visionAndImages": "Vision & Images",
    "advancedSettings.visionBackend": "Vision Backend",
    "advancedSettings.visionAuto": "Auto",
    "advancedSettings.visionOpenAI": "OpenAI",
    "advancedSettings.visionBLIP": "BLIP",
    "advancedSettings.visionNone": "None",
    "advancedSettings.maxImageWidth": "Max Image Width (px)",
    "advancedSettings.inlineAssets": "Inline Assets (Base64, larger HTML)",
    "advancedSettings.processingOptions": "Processing Options",
    "advancedSettings.debugLevel": "Debug Level",
    "advancedSettings.debugDebug": "Debug",
    "advancedSettings.debugInfo": "Info",
    "advancedSettings.debugWarning": "Warning",
    "advancedSettings.debugError": "Error",
    "advancedSettings.debugCritical": "Critical",
    "advancedSettings.skipSummary": "Skip Summary Generation",
    "advancedSettings.rateLimiting": "Rate Limiting",
    "advancedSettings.minDelay": "Min Delay (sec)",
    "advancedSettings.maxDelay": "Max Delay (sec)",
    "advancedSettings.maxRetries": "Max Retries",
    "advancedSettings.backoffMultiplier": "Backoff Multiplier",
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem("language");
    return (saved as Language) || "he";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === "he" ? "rtl" : "ltr";
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.he] || key;
  };

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
